import { CorePropsInterfaces } from "src/core/Models";
import { t } from "i18next";
import LinkIcon from "@mui/icons-material/Link";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { Button, Tooltip } from "@mui/material";
import { AppContext } from "src/contexts/AppContext";
import { useContext } from "react";
import * as Constants from "src/core/Constants/Constants";
import { convertStringIntoDate } from "src/utils";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { CoreInterfaces } from "src/core/Models";
import { isAdministrator } from "src/utils/user-roles-utils";

function GtEngagementLetterSignees(
  props: CorePropsInterfaces.GtEngagementLetterSigneesProps
) {
  const { globalState } = useContext(AppContext);

  const {
    engagementLetterSignees,
    envelopeIds,
    cancelSigneeHandler,
    downloadDocumentHandler,
    linkHandler,
    editHandler,
    notifyHandler,
    isVisibleDownloadBtn,
    isSigningInProgress,
  } = props;

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  };

  const signingStatus = globalState.currentConfiguration.signingStatus
    ? globalState.currentConfiguration.signingStatus
    : "";

  function isObsolete(): boolean {
    return [Constants.SigningStatus.SignedObsolete].includes(
      globalState.currentConfiguration.signingStatus as Constants.SigningStatus
    );
  }

  function isSignedOrRejected(
    engagementLetterSignee: CoreInterfaces.EngagementLetterSignee
  ): boolean {
    const status = engagementLetterSignee.RecipientStatusName?.toLowerCase();
    return status === "signed" || status === "rejected";
  }

  function isGTEmployee(email: string): boolean {
    return (
      email?.toLowerCase().endsWith(Constants.GT_EMPLOYEE_EMAIL_DOMAIN) ?? false
    );
  }

  function compareByName(
    a: CoreInterfaces.EngagementLetterSignee,
    b: CoreInterfaces.EngagementLetterSignee
  ): number {
    const nameA = `${a.FirstName} ${a.LastName}`.toLowerCase();
    const nameB = `${b.FirstName} ${b.LastName}`.toLowerCase();
    return nameA.localeCompare(nameB);
  }

  const clients = engagementLetterSignees.filter((s) => !isGTEmployee(s.Email));
  const gtEmployees = engagementLetterSignees.filter((s) =>
    isGTEmployee(s.Email)
  );

  const sortedSignees = [
    ...clients.slice().sort(compareByName),
    ...gtEmployees.slice().sort(compareByName),
  ];

  const allClientsSigned =
    clients.length > 0 &&
    clients.every((s) => s.RecipientStatusName?.toLowerCase() === "signed");

  return (
    <article>
      <section className="gt-engagementLetterSigneesList">
        <article className="gt-engagementLetterSigneesListHeader">
          <section className="gt-engagementLetterSigneesListHeader__leftSide">
            <article
              className={`gt-engagementLetterSigneesStatus gt-engagementLetterSigneesStatus--${"signed"}`}
            >
              {t(`General.${signingStatus}Text`, {
                date: convertStringIntoDate(
                  globalState.currentConfiguration.signingDate
                ),
              })}
            </article>
            <div className="gt-engagementLetterSigneesListHeader__leftSide-title">
              <span>{t("General.EngagementLetterSignees")}</span>
              <Tooltip title={t("General.EngagementLetterSigneesTooltip")}>
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            </div>
          </section>
          <section className="gt-engagementLetterSigneesListHeader__rightSide">
            {isVisibleDownloadBtn && (
              <Button
                color="primary"
                variant="outlined"
                key="downloadPdfBtn"
                onClick={() => downloadDocumentHandler()}
              >
                {t("General.DownloadPDF")}
              </Button>
            )}
            {isSigningInProgress && (
              <Button
                color="error"
                variant="outlined"
                key="cancelSignProcessBtn"
                onClick={() => cancelSigneeHandler()}
              >
                {t("General.CancelSignProcess")}
              </Button>
            )}
          </section>
        </article>
        {sortedSignees.map((engagementLetterSignee, index) => (
          <article key={index} className="gt-engagementLetterSigneesListItem">
            <section className="gt-engagementLetterSigneesListItem__leftSide">
              <section className="gt-engagementLetterSigneesListItem__leftSide-name">
                {engagementLetterSignee.FirstName}{" "}
                {engagementLetterSignee.LastName}
              </section>
              <section className="gt-engagementLetterSigneesListItem__leftSide-info">
                <article className="gt-engagementLetterSigneesListKeyValue">
                  <section className="gt-engagementLetterSigneesListKeyValue__key">
                    {t("General.Email")}:
                  </section>
                  <section className="gt-engagementLetterSigneesListKeyValue__value">
                    {engagementLetterSignee.Email}
                  </section>
                </article>
                <article className="gt-engagementLetterSigneesListKeyValue">
                  <section className="gt-engagementLetterSigneesListKeyValue__key">
                    {t("General.Date")}:
                  </section>
                  <section className="gt-engagementLetterSigneesListKeyValue__value">
                    {new Date(
                      engagementLetterSignee.RecipientStatusTimestamp
                    ).toLocaleDateString([], options)}
                  </section>
                </article>
                {envelopeIds &&
                  envelopeIds[engagementLetterSignee.Id] &&
                  isAdministrator(globalState) && (
                    <article className="gt-engagementLetterSigneesListKeyValue">
                      <section className="gt-engagementLetterSigneesListKeyValue__key">
                        Verified ID:
                      </section>
                      <section className="gt-engagementLetterSigneesListKeyValue__value">
                        {envelopeIds[engagementLetterSignee.Id]}
                      </section>
                    </article>
                  )}
              </section>
            </section>
            <section className="gt-engagementLetterSigneesListItem__rightSide">
              <article
                className={`gt-engagementLetterSigneesStatus gt-engagementLetterSigneesStatus--${engagementLetterSignee.RecipientStatusName?.toLowerCase()}`}
              >
                {engagementLetterSignee.RecipientStatusName}
              </article>
              {!isObsolete() && (
                <>
                  <Tooltip
                    title={t("General.EngagementLetterSigneesLinkTooltip")}
                  >
                    <a
                      className="gt-engagementLetterSigneesListItemIcon"
                      onClick={() => linkHandler(engagementLetterSignee)}
                      href="#"
                    >
                      <LinkIcon fontSize="small" />
                    </a>
                  </Tooltip>
                  {!isSignedOrRejected(engagementLetterSignee) && (
                    <>
                      <Tooltip
                        title={t(
                          "General.EngagementLetterSigneesEditEmailTooltip"
                        )}
                      >
                        <a
                          className="gt-engagementLetterSigneesListItemIcon"
                          onClick={() => editHandler(engagementLetterSignee)}
                          href="#"
                        >
                          <DriveFileRenameOutlineIcon fontSize="small" />
                        </a>
                      </Tooltip>
                      <Tooltip
                        title={t(
                          "General.EngagementLetterSigneesNotifyTooltip"
                        )}
                      >
                        <a
                          className="gt-engagementLetterSigneesListItemIcon"
                          onClick={() => notifyHandler(engagementLetterSignee)}
                          href="#"
                        >
                          <NotificationsNoneIcon fontSize="small" />
                        </a>
                      </Tooltip>
                    </>
                  )}
                </>
              )}
              {isGTEmployee(engagementLetterSignee.Email) &&
                !allClientsSigned && (
                  <Tooltip title={t("General.GTEmployeeWaitingForClients")}>
                    <span className="gt-engagementLetterSigneesListItemIcon gt-engagementLetterSigneesWaitingIcon">
                      <InfoOutlinedIcon fontSize="small" />
                    </span>
                  </Tooltip>
                )}
            </section>
          </article>
        ))}
      </section>
    </article>
  );
}

export default GtEngagementLetterSignees;
